import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertCircle, Clock, CheckSquare, Folder, Target, TrendingUp, BarChart3,
  Calendar, Users, RefreshCw, Plus, X, CheckCircle, AlertTriangle,
  ArrowRight, User, DollarSign, Layers, ChevronDown, ChevronUp,
  Zap, Award, Activity, PieChart, Eye, TrendingDown, Briefcase,
  Euro, Hash, Flag, Home, Building2, UserX, Wifi, GraduationCap, UserCheck,
  Check, ChevronLeft, ChevronRight, Truck
} from 'lucide-react';
import { API_URL } from '../config';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmt = (n, decimals = 1) => (n == null || n === '') ? '—' : Number(n).toLocaleString('fr-FR', { maximumFractionDigits: decimals });
const fmtEuro = (n) => (n == null || n === '') ? '—' : Number(n).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const daysLeft = (endDate) => {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
};

// ─── Statuts tâches ────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  'À faire':   { bg: 'bg-elevated-1',   text: 'text-text-secondary',   dot: 'bg-gray-400' },
  'En cours':  { bg: 'bg-blue-100 dark:bg-blue-900/40',   text: 'text-blue-700 dark:text-blue-300',   dot: 'bg-blue-500' },
  'En retard': { bg: 'bg-red-100 dark:bg-red-900/40',    text: 'text-red-700 dark:text-red-300',    dot: 'bg-red-500' },
  'Terminé':   { bg: 'bg-green-100 dark:bg-green-900/40',  text: 'text-green-700 dark:text-green-300',  dot: 'bg-green-500' },
  'Annulée':   { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300', dot: 'bg-orange-400' },
};
const getStatusStyle = (s) => STATUS_STYLES[s] || STATUS_STYLES['À faire'];

// ─── StatCard ──────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon: Icon, gradient, onClick, alert }) => (
  <div
    className={`relative rounded-2xl p-3 sm:p-5 text-white shadow-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform ${gradient}`}
    onClick={onClick}
  >
    {alert && (
      <span className="absolute top-2 right-2 w-3 h-3 bg-yellow-300 rounded-full animate-pulse" />
    )}
    <div className="flex items-center justify-between">
      <div>
        <p className="text-white/80 text-xs font-medium uppercase tracking-wide">{label}</p>
        <p className="text-3xl sm:text-4xl font-bold mt-1 leading-none">{value}</p>
        {sub && <p className="text-white/70 text-xs mt-1">{sub}</p>}
      </div>
      <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white/30" />
    </div>
  </div>
);

// ─── ProgressBar ───────────────────────────────────────────────────────────────
const ProgressBar = ({ pct, color = 'bg-blue-500', height = 'h-2' }) => (
  <div className={`w-full bg-elevated-1 rounded-full ${height} overflow-hidden`}>
    <div
      className={`${height} rounded-full transition-all duration-500 ${color}`}
      style={{ width: `${Math.min(100, Math.max(0, pct || 0))}%` }}
    />
  </div>
);

const pctColor = (pct) =>
  pct >= 100 ? 'bg-emerald-500' : pct >= 66 ? 'bg-blue-500' : pct >= 33 ? 'bg-amber-500' : 'bg-red-400';

// ─── SectionHeader ─────────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, count, color, badge, action }) => (
  <div className={`px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between border-b ${color}`}>
    <div className="flex items-center gap-3">
      <Icon className="w-5 h-5" />
      <h2 className="text-base font-bold">{title}</h2>
      {count !== undefined && (
        <span className="bg-white/60 text-current text-xs font-bold px-2 py-0.5 rounded-full">{count}</span>
      )}
      {badge}
    </div>
    {action}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// DashboardPatron
// ─────────────────────────────────────────────────────────────────────────────
const DashboardPatron = ({ authToken, employees = [], authUser, onNavigate }) => {
  const [data, setData]               = useState(null);
  const [kpiGlobal, setKpiGlobal]     = useState(null);
  const [loading, setLoading]         = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [effectifs, setEffectifs]     = useState(null);
  const [presenceData, setPresenceData] = useState({ pointages: [], schedules: [] });
  const [expandedProj, setExpandedProj] = useState([]);
  const [expandedObj,  setExpandedObj]  = useState([]);
  const [kpiEvents, setKpiEvents]     = useState(null);
  const [objAlerts, setObjAlerts]     = useState([]);

  // Quick-action modal
  const [showAddAction,  setShowAddAction]  = useState(false);
  const [targetObjectif, setTargetObjectif] = useState(null);
  const [actionForm, setActionForm]         = useState({ title: '', assigned_to: '', due_date: '', priority: 'Moyenne' });
  const [savingAction, setSavingAction]     = useState(false);

  const headers = { 'Authorization': `Bearer ${authToken}` };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [patronRes, effectifsRes, kpiRes] = await Promise.all([
        fetch(`${API_URL}/dashboard/patron`,     { headers }),
        fetch(`${API_URL}/dashboard/effectifs`,  { headers }),
        fetch(`${API_URL}/dashboard/kpi-global`, { headers }).catch(() => null),
      ]);
      if (!patronRes.ok) throw new Error('Erreur API patron');
      setData(await patronRes.json());
      if (effectifsRes.ok) setEffectifs(await effectifsRes.json());
      if (kpiRes && kpiRes.ok) setKpiGlobal(await kpiRes.json()); // { projects, grand_total_*, alerts }

      // Charger pointages et plannings du jour pour les présences
      const todayStr = new Date().toISOString().split('T')[0];
      try {
        const [pointagesRes, schedulesRes] = await Promise.all([
          fetch(`${API_URL}/pointages`, { headers }),
          fetch(`${API_URL}/schedules?start_date=${todayStr}&end_date=${todayStr}`, { headers }),
        ]);
        const ptgs = pointagesRes.ok ? await pointagesRes.json() : [];
        const scheds = schedulesRes.ok ? await schedulesRes.json() : [];
        setPresenceData({ pointages: ptgs, schedules: scheds });
      } catch (e) {
        console.error('Erreur chargement présences:', e);
      }

      // Charger les KPI événements : mois en cours + événements à venir
      try {
        const now = new Date();
        const evtRes = await fetch(`${API_URL}/kpi/evenements?year=${now.getFullYear()}&month=${now.getMonth() + 1}`, { headers });
        let evtData = evtRes.ok ? await evtRes.json() : { nombre_evenements: 0, ca_total: 0, evenements: [], top_extras: [] };

        // Charger aussi les événements des 3 prochains mois pour avoir les "à venir"
        const upcoming = [];
        for (let i = 1; i <= 3; i++) {
          const futureDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
          const futRes = await fetch(`${API_URL}/kpi/evenements?year=${futureDate.getFullYear()}&month=${futureDate.getMonth() + 1}`, { headers });
          if (futRes.ok) {
            const futData = await futRes.json();
            if (futData.evenements) upcoming.push(...futData.evenements);
          }
        }
        evtData.upcoming = upcoming;
        setKpiEvents(evtData);
      } catch (e) {
        console.error('Erreur KPI événements:', e);
      }

      // Charger les alertes objectifs
      try {
        const alertsRes = await fetch(`${API_URL}/objectifs-alerts`, { headers });
        if (alertsRes.ok) setObjAlerts(await alertsRes.json());
      } catch (e) {
        console.error('Erreur alertes objectifs:', e);
      }

      setLastRefresh(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authToken]); // eslint-disable-line

  useEffect(() => {
    if (authToken) {
      loadDashboard();
      const t = setInterval(loadDashboard, 60000);
      return () => clearInterval(t);
    }
  }, [authToken, loadDashboard]);

  const submitAddAction = async (e) => {
    e.preventDefault();
    if (!actionForm.title.trim()) return;
    setSavingAction(true);
    try {
      const res = await fetch(`${API_URL}/tasks`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: actionForm.title.trim(),
          assigned_to: actionForm.assigned_to || null,
          assigned_by: authUser?.employee_id,
          objectif_id: targetObjectif?.id,
          project_id: targetObjectif?.project_id || null,
          due_date: actionForm.due_date || null,
          priority: actionForm.priority,
          status: 'À faire',
        }),
      });
      if (!res.ok) { const j = await res.json(); throw new Error(j.error); }
      setShowAddAction(false);
      loadDashboard();
    } catch (err) { alert(err.message); }
    finally { setSavingAction(false); }
  };

  // ── Chargement ──────────────────────────────────────────────────────────────
  if (loading && !data) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-24 bg-gray-200 rounded-2xl" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="h-24 bg-gray-200 rounded-2xl" />
          <div className="h-24 bg-gray-200 rounded-2xl" />
          <div className="h-24 bg-gray-200 rounded-2xl" />
          <div className="h-24 bg-gray-200 rounded-2xl" />
        </div>
        <div className="h-48 bg-gray-200 rounded-2xl" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-40 bg-gray-200 rounded-2xl" />
          <div className="h-40 bg-gray-200 rounded-2xl" />
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 rounded-xl p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-700 dark:text-red-300 font-medium mb-3">Impossible de charger le tableau de bord</p>
        <button onClick={loadDashboard} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm">
          Réessayer
        </button>
      </div>
    );
  }

  const { taches_en_retard = [], taches_en_cours = [], projets = [], objectifs = [], stats = {} } = data;

  const toggleProj = (id) => setExpandedProj(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleObj  = (id) => setExpandedObj(p  => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  // Compteurs rapides
  const projRetard = projets.filter(p => {
    const dl = daysLeft(p.end_date);
    return dl !== null && dl < 0 && p.status === 'En cours';
  }).length;
  const objAlert = objectifs.filter(o =>
    o.chiffrage > 0 && (o.valeur_actuelle / o.chiffrage) < 0.3 && o.date_fin && daysLeft(o.date_fin) < 14
  ).length;

  // KPI finance data
  const kpiProjects = kpiGlobal?.projects || [];
  const kpiAlerts   = kpiGlobal?.alerts || [];
  const grandTotalTarget    = kpiGlobal?.grand_total_target    || 0;
  const grandTotalRealise   = kpiGlobal?.grand_total_realise_ht || 0;
  const grandTotalTTC       = kpiGlobal?.grand_total_realise_ttc || 0;
  const grandTotalSign      = kpiGlobal?.grand_total_signatures  || 0;
  const pourcentageGlobal   = kpiGlobal?.pourcentage_global || 0;

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5 pb-10">

      {/* ═══ MODAL AJOUT ACTION ═══════════════════════════════════════════════ */}
      {showAddAction && targetObjectif && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <div>
                <h2 className="text-lg font-bold text-text-primary">Ajouter une action rapide</h2>
                <p className="text-xs text-violet-600 dark:text-violet-400 mt-0.5">Objectif : {targetObjectif.titre}</p>
              </div>
              <button onClick={() => setShowAddAction(false)}>
                <X className="w-5 h-5 text-text-muted hover:text-text-secondary" />
              </button>
            </div>
            <form onSubmit={submitAddAction} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Titre *</label>
                  <input required autoFocus
                    className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] focus:ring-2 focus:ring-violet-400"
                    placeholder="Décrire l'action…"
                    value={actionForm.title}
                    onChange={e => setActionForm(p => ({ ...p, title: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Assigné à</label>
                  <select
                    className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px]"
                    value={actionForm.assigned_to}
                    onChange={e => setActionForm(p => ({ ...p, assigned_to: e.target.value }))}
                  >
                    <option value="">— Sélectionner —</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}{emp.id === authUser?.employee_id ? ' (moi)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Échéance</label>
                    <input type="date" className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px]"
                      value={actionForm.due_date}
                      onChange={e => setActionForm(p => ({ ...p, due_date: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Priorité</label>
                    <select className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px]"
                      value={actionForm.priority}
                      onChange={e => setActionForm(p => ({ ...p, priority: e.target.value }))}>
                      <option>Faible</option><option>Moyenne</option><option>Haute</option><option>Critique</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                <button type="button" onClick={() => setShowAddAction(false)}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center text-sm border border-border-strong hover:bg-elevated-1">Annuler</button>
                <button type="submit" disabled={savingAction}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center text-sm bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                  {savingAction ? 'Création…' : "Créer l'action"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ HEADER ════════════════════════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-violet-700 via-purple-600 to-indigo-600 rounded-2xl p-4 sm:p-6 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">Tableau de Bord Patron</h1>
            <p className="text-violet-200 text-sm mt-0.5">
              Vue synthétique · {lastRefresh ? `Mis à jour ${lastRefresh.toLocaleTimeString('fr-FR')}` : 'Chargement…'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Pointeuse', icon: Clock,       view: 'pointeuse' },
              { label: 'Projets',   icon: Folder,      view: 'projets' },
              { label: 'Tâches',    icon: CheckSquare, view: 'taches' },
              { label: 'Objectifs', icon: Target,      view: 'objectifs' },
            ].map(({ label, icon: Icon, view }) => onNavigate && (
              <button key={view}
                onClick={() => onNavigate(view)}
                className="bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-2 sm:py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition min-h-[44px] sm:min-h-0"
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
            <button onClick={loadDashboard}
              className="bg-white/15 hover:bg-white/25 p-2.5 sm:p-2 rounded-lg transition min-h-[44px] min-w-[44px] flex items-center justify-center" title="Rafraîchir">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* ═══ STAT CARDS ════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard label="Tâches en retard" value={stats.total_taches_retard || 0}
          sub={taches_en_retard.length > 0 ? 'Action requise !' : 'RAS'}
          icon={AlertCircle}
          gradient="bg-gradient-to-br from-red-500 to-rose-600"
          onClick={() => onNavigate?.('taches')} alert={taches_en_retard.length > 0} />
        <StatCard label="Tâches en cours" value={stats.total_taches_en_cours || 0}
          sub="En progression" icon={Activity}
          gradient="bg-gradient-to-br from-blue-500 to-blue-700"
          onClick={() => onNavigate?.('taches')} />
        <StatCard label="Projets actifs" value={stats.total_projets_actifs || 0}
          sub={projRetard > 0 ? `${projRetard} en retard` : 'Dans les temps'}
          icon={Folder}
          gradient="bg-gradient-to-br from-violet-500 to-violet-700"
          onClick={() => onNavigate?.('projets')} alert={projRetard > 0} />
        <StatCard label="Objectifs actifs" value={stats.total_objectifs_actifs || 0}
          sub={objAlert > 0 ? `${objAlert} sous 30%` : 'Bien suivis'}
          icon={Target}
          gradient="bg-gradient-to-br from-amber-500 to-orange-600"
          onClick={() => onNavigate?.('objectifs')} alert={objAlert > 0} />
      </div>

      {/* ═══ ALERTES OBJECTIFS ════════════════════════════════════════════════ */}
      {objAlerts.length > 0 && (
        <div className="bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 border-red-400">
          <div className="bg-red-50 dark:bg-red-950/40 px-3 sm:px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-800">Alertes Objectifs</span>
              <span className="bg-red-200 dark:bg-red-800/40 text-red-800 text-xs rounded-full px-2 py-0.5">{objAlerts.length}</span>
            </div>
            <button onClick={() => onNavigate?.('objectifs')}
              className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:text-red-200 flex items-center gap-1">
              Voir tout <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="p-3 sm:p-4 space-y-2 max-h-64 overflow-y-auto">
            {objAlerts.slice(0, 8).map(alert => (
              <div key={alert.id} className={`flex items-center gap-3 p-3 rounded-lg ${
                alert.alert_type === 'overdue' ? 'bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800' : 'bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800'
              }`}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${alert.alert_type === 'overdue' ? 'bg-red-500' : 'bg-amber-500'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{alert.titre}</p>
                  <div className="flex items-center gap-2 text-xs text-text-muted mt-0.5">
                    {alert.project_name && <span className="flex items-center gap-1"><Folder className="w-3 h-3" />{alert.project_name}</span>}
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(alert.date_fin)}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {alert.alert_type === 'overdue' ? (
                    <span className="text-xs font-semibold text-red-700 bg-red-100 dark:bg-red-900/40 px-2 py-0.5 rounded-full">
                      {Math.abs(alert.days_remaining)}j en retard
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-amber-700 bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 rounded-full">
                      J-{alert.days_remaining}
                    </span>
                  )}
                  <ProgressBar pct={alert.progression || 0} color={alert.alert_type === 'overdue' ? 'bg-red-400' : 'bg-amber-400'} height="h-1.5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ 1. PRÉSENCES DU JOUR ════════════════════════════════════════════ */}
      {(() => {
        const todayStr = new Date().toISOString().split('T')[0];
        const todayPointages = presenceData.pointages.filter(p => p.date === todayStr && p.statut === 'en_cours');
        const presentIds = new Set(todayPointages.map(p => p.user_id));
        const todaySchedules = presenceData.schedules.filter(s => s.date === todayStr);

        const presenceList = employees
          .filter(e => e.role_name !== 'Extra')
          .map(emp => {
            const isPresent = presentIds.has(emp.id);
            const schedule = todaySchedules.find(s => s.employee_id === emp.id);
            const entryType = schedule?.entry_type || schedule?.shift_type || null;
            const isTeletravail = entryType === 'Télétravail';
            const isFormation = entryType === 'Formation';

            let status, statusColor, statusIcon, statusLabel;
            if (isFormation) {
              status = 'formation';
              statusColor = 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200';
              statusIcon = <GraduationCap className="w-3.5 h-3.5" />;
              statusLabel = 'Formation';
            } else if (isTeletravail) {
              status = 'teletravail';
              statusColor = 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200';
              statusIcon = <Home className="w-3.5 h-3.5" />;
              statusLabel = 'Télétravail';
            } else if (isPresent) {
              status = 'depot';
              statusColor = 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200';
              statusIcon = <Building2 className="w-3.5 h-3.5" />;
              statusLabel = 'Au dépôt';
            } else {
              status = 'absent';
              statusColor = 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
              statusIcon = <UserX className="w-3.5 h-3.5" />;
              statusLabel = 'Absent';
            }

            const pointage = todayPointages.find(p => p.user_id === emp.id);
            const arrival = pointage?.heure_arrivee;

            return { ...emp, status, statusColor, statusIcon, statusLabel, arrival, entryType };
          })
          .sort((a, b) => {
            const order = { depot: 0, teletravail: 1, formation: 2, absent: 3 };
            return (order[a.status] ?? 4) - (order[b.status] ?? 4);
          });

        const countDepot = presenceList.filter(e => e.status === 'depot').length;
        const countTele = presenceList.filter(e => e.status === 'teletravail').length;
        const countFormation = presenceList.filter(e => e.status === 'formation').length;
        const countAbsent = presenceList.filter(e => e.status === 'absent').length;
        const countPresent = countDepot + countTele + countFormation;

        return (
          <div className="bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 border-blue-400">
            <SectionHeader
              icon={Users}
              title="Présences du jour"
              color="bg-blue-50 dark:bg-blue-950/40 text-blue-800"
              count={`${countPresent}/${presenceList.length}`}
              action={
                <button onClick={() => onNavigate?.('pointeuse')}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:text-blue-200 flex items-center gap-1">
                  Pointeuse <ArrowRight className="w-3 h-3" />
                </button>
              }
            />
            <div className="p-3 sm:p-4">
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-950/40 rounded-full">
                  <Building2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-semibold text-green-700">{countDepot}</span>
                  <span className="text-xs text-green-600 dark:text-green-400">au dépôt</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-50 dark:bg-cyan-950/40 rounded-full">
                  <Home className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                  <span className="text-sm font-semibold text-cyan-700">{countTele}</span>
                  <span className="text-xs text-cyan-600 dark:text-cyan-400">télétravail</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 dark:bg-purple-950/40 rounded-full">
                  <GraduationCap className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <span className="text-sm font-semibold text-purple-700">{countFormation}</span>
                  <span className="text-xs text-purple-600 dark:text-purple-400">formation</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 dark:bg-red-950/40 rounded-full">
                  <UserX className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-semibold text-red-600 dark:text-red-400">{countAbsent}</span>
                  <span className="text-xs text-red-500">absent{countAbsent > 1 ? 's' : ''}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {presenceList.map(emp => (
                  <div key={emp.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-elevated-1 hover:bg-elevated-1 transition">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-xs shrink-0">
                      {emp.avatar || (emp.first_name?.[0] || '') + (emp.last_name?.[0] || '')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{emp.first_name} {emp.last_name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${emp.statusColor}`}>
                          {emp.statusIcon}
                          {emp.statusLabel}
                        </span>
                        {emp.arrival && (
                          <span className="text-[11px] text-text-muted">
                            {emp.arrival.slice(0, 5)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ 2. EFFECTIFS OBLIGATOIRES ════════════════════════════════════════ */}
      {effectifs && (() => {
        const DAY_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        const sStyle = {
          ok:      { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500', label: 'Couvert' },
          partial: { bg: 'bg-amber-50 dark:bg-amber-950/40',   text: 'text-amber-700 dark:text-amber-300',   dot: 'bg-amber-500',   label: 'Partiel' },
          absent:  { bg: 'bg-red-50 dark:bg-red-950/40',     text: 'text-red-700 dark:text-red-300',     dot: 'bg-red-500',     label: 'Absent'  },
        };
        const effectifsFiltered = Object.values(effectifs).filter(s => s.label !== 'Administration');
        const hasAlert = effectifsFiltered.some(s => s.days && s.days.some(d => d.status !== 'ok'));
        return (
          <div className={`bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 ${hasAlert ? 'border-amber-400' : 'border-emerald-400'}`}>
            <SectionHeader
              icon={Users} title="Effectifs obligatoires"
              color={hasAlert ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-800' : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200'}
              badge={hasAlert
                ? <span className="bg-amber-200 dark:bg-amber-800/40 text-amber-800 dark:text-amber-200 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Alerte</span>
                : <span className="bg-emerald-200 dark:bg-emerald-800/40 text-emerald-800 dark:text-emerald-200 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" />OK</span>
              }
            />
            <div className="p-3 sm:p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left text-text-muted font-medium pb-2 pr-4 w-40 text-xs">Service</th>
                    {effectifsFiltered[0]?.days?.map(d => {
                      const dt = new Date(d.date + 'T00:00:00');
                      const isToday = d.date === new Date().toISOString().split('T')[0];
                      return (
                        <th key={d.date} className={`text-center pb-2 px-2 min-w-[80px] text-xs ${isToday ? 'text-blue-700 dark:text-blue-300 font-bold' : 'text-text-muted font-medium'}`}>
                          <div>{DAY_FR[dt.getDay()]}</div>
                          <div className="font-normal">{dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</div>
                          {isToday && <div className="text-blue-400 text-[10px]">Aujourd'hui</div>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {effectifsFiltered.map(srv => (
                    <tr key={srv.label} className="border-t border-border-subtle">
                      <td className="py-2 pr-4">
                        <div className="font-semibold text-text-secondary text-sm">{srv.label}</div>
                        <div className="text-xs text-text-muted">{srv.req_start}–{srv.req_end}</div>
                      </td>
                      {srv.days && srv.days.map(d => {
                        const st = sStyle[d.status] || sStyle.absent;
                        return (
                          <td key={d.date} className="py-2 px-2 text-center">
                            <div className={`inline-flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg ${st.bg}`}>
                              <span className={`flex items-center gap-1 text-[11px] font-semibold ${st.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                                {st.label}
                              </span>
                              {d.people && d.people.length > 0 && (
                                <span className="text-[10px] text-text-muted max-w-[70px] truncate" title={d.people.join(', ')}>
                                  {d.people.length === 1 ? d.people[0].split(' ')[0] : `${d.people.length} pers.`}
                                </span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ═══ TÂCHES ÉQUIPE — VUE JOUR/SEMAINE COMPACTE ═════════════════════ */}
      {(() => {
        const todayStrTeam = new Date().toISOString().split('T')[0];

        // Helpers locaux
        const getWeekDays = (dateStr) => {
          const d = new Date(dateStr + 'T00:00:00');
          const dow = d.getDay();
          const monOff = dow === 0 ? -6 : 1 - dow;
          const mon = new Date(d); mon.setDate(d.getDate() + monOff);
          return Array.from({ length: 7 }, (_, i) => {
            const day = new Date(mon); day.setDate(mon.getDate() + i);
            return day.toISOString().split('T')[0];
          });
        };

        const TEAM_COLORS = [
          'bg-blue-500','bg-emerald-500','bg-purple-500','bg-amber-500',
          'bg-rose-500','bg-cyan-500','bg-orange-500','bg-indigo-500'
        ];

        const TeamWidget = () => {
          const [teamView, setTeamView] = useState('jour');
          const [teamDateLocal, setTeamDateLocal] = useState(todayStrTeam);
          const [allTasks, setAllTasks] = useState([]);

          useEffect(() => {
            fetch(`${API_URL}/tasks`, { headers })
              .then(r => r.ok ? r.json() : [])
              .then(d => setAllTasks((Array.isArray(d) ? d : []).filter(t => !t.parent_task_id && t.status !== 'Annulée')))
              .catch(() => setAllTasks([]));
          }, []);

          const getTasksForEmpDay = (empId, dateStr) => {
            return allTasks.filter(t => {
              if (parseInt(t.assigned_to) !== empId) return false;
              if (t.scheduled_date && t.scheduled_start_time && t.scheduled_end_time) return t.scheduled_date === dateStr;
              const dd = t.due_date ? t.due_date.split('T')[0] : null;
              return dd === dateStr;
            });
          };

          const navTeam = (offset) => {
            const d = new Date(teamDateLocal);
            if (teamView === 'semaine') d.setDate(d.getDate() + offset * 7);
            else d.setDate(d.getDate() + offset);
            setTeamDateLocal(d.toISOString().split('T')[0]);
          };

          const dateLabel = (() => {
            const d = new Date(teamDateLocal + 'T00:00:00');
            if (teamView === 'semaine') {
              const days = getWeekDays(teamDateLocal);
              const mon = new Date(days[0] + 'T00:00:00');
              const sun = new Date(days[6] + 'T00:00:00');
              return `${mon.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} — ${sun.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
            }
            return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' });
          })();

          const activeEmps = employees.filter(e => !e.deleted_at && e.role_name !== 'Extra');
          const wDays = getWeekDays(teamDateLocal);

          return (
            <div className="bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 border-indigo-400">
              <div className="px-3 sm:px-5 py-2.5 flex flex-col sm:flex-row items-start sm:items-center justify-between bg-indigo-50 dark:bg-indigo-950/40 border-b gap-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-indigo-700 dark:text-indigo-300" />
                  <h3 className="text-sm font-bold text-indigo-800 dark:text-indigo-200">Tâches de l'équipe</h3>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex rounded-lg border overflow-hidden">
                    {['jour','semaine'].map(v => (
                      <button key={v} onClick={() => setTeamView(v)}
                        className={`px-2.5 py-1.5 text-xs font-medium transition capitalize ${teamView === v ? 'bg-indigo-600 text-white' : 'bg-surface text-text-secondary hover:bg-elevated-1'}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => navTeam(-1)} className="p-1 rounded hover:bg-elevated-1"><ChevronLeft className="w-4 h-4" /></button>
                  <span className="text-xs font-semibold text-text-secondary min-w-[120px] text-center capitalize">{dateLabel}</span>
                  <button onClick={() => navTeam(1)} className="p-1 rounded hover:bg-elevated-1"><ChevronRight className="w-4 h-4" /></button>
                  <button onClick={() => setTeamDateLocal(todayStrTeam)} className="text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline">Auj.</button>
                </div>
              </div>

              <div className="max-h-[500px] overflow-y-auto">
                {/* Vue Jour */}
                {teamView === 'jour' && (
                  <div className="divide-y">
                    {activeEmps.map((emp, empIdx) => {
                      const empTasks = getTasksForEmpDay(emp.id, teamDateLocal)
                        .sort((a, b) => (a.scheduled_start_time || 'zz').localeCompare(b.scheduled_start_time || 'zz'));
                      if (empTasks.length === 0) return null;
                      return (
                        <div key={emp.id} className="px-3 sm:px-4 py-2.5">
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className={`w-2 h-2 rounded-full ${TEAM_COLORS[empIdx % TEAM_COLORS.length]}`} />
                            <span className="text-xs font-bold text-text-primary">{emp.first_name} {emp.last_name}</span>
                            <span className="text-[10px] text-text-muted">{emp.service_name}</span>
                          </div>
                          <div className="space-y-1 pl-4">
                            {empTasks.map(t => {
                              const isDone = t.status === 'Terminé';
                              const overdue = t.due_date && new Date(t.due_date) < new Date() && !isDone;
                              const sStyle = getStatusStyle(t.status);
                              const hasTime = t.scheduled_start_time && t.scheduled_end_time;
                              return (
                                <div key={t.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${isDone ? 'bg-green-50 dark:bg-green-950/40' : overdue ? 'bg-red-50 dark:bg-red-950/40' : 'bg-elevated-1'}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sStyle.dot}`} />
                                  {hasTime && (
                                    <span className={`font-mono text-[10px] flex-shrink-0 ${isDone ? 'text-text-muted' : 'text-indigo-600 dark:text-indigo-400'}`}>
                                      {t.scheduled_start_time}-{t.scheduled_end_time}
                                    </span>
                                  )}
                                  {!hasTime && <span className="text-[10px] text-text-muted italic flex-shrink-0">journée</span>}
                                  <span className={`font-medium truncate ${isDone ? 'line-through text-text-muted' : overdue ? 'text-red-700 dark:text-red-300' : 'text-text-primary'}`}>{t.title}</span>
                                  {t.vehicule_nom && <Truck className="w-3 h-3 text-orange-500 flex-shrink-0" />}
                                  <span className={`ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-medium ${sStyle.bg} ${sStyle.text} flex-shrink-0`}>{t.status}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {activeEmps.every(emp => getTasksForEmpDay(emp.id, teamDateLocal).length === 0) && (
                      <div className="py-8 text-center text-text-muted text-sm">Aucune tâche attribuée ce jour</div>
                    )}
                  </div>
                )}

                {/* Vue Semaine — compact */}
                {teamView === 'semaine' && (
                  <>
                    {/* Desktop : tableau */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-elevated-1">
                            <th className="sticky left-0 bg-elevated-1 z-10 border-b border-r px-2 py-2 text-left font-semibold text-text-secondary w-32">Employé</th>
                            {wDays.map(day => {
                              const d = new Date(day + 'T00:00:00');
                              const isT = day === todayStrTeam;
                              return (
                                <th key={day} className={`border-b border-r px-1.5 py-2 text-center font-semibold min-w-[120px] ${isT ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300' : 'text-text-secondary'}`}>
                                  <div>{d.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                                  <div className="text-sm">{d.getDate()}</div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {activeEmps.map((emp, empIdx) => {
                            const weekHasTasks = wDays.some(d => getTasksForEmpDay(emp.id, d).length > 0);
                            if (!weekHasTasks) return null;
                            return (
                              <tr key={emp.id} className={empIdx % 2 === 0 ? 'bg-surface' : 'bg-elevated-1/50'}>
                                <td className="sticky left-0 bg-inherit z-10 border-b border-r px-2 py-1.5 whitespace-nowrap">
                                  <div className="flex items-center gap-1.5">
                                    <div className={`w-2 h-2 rounded-full ${TEAM_COLORS[empIdx % TEAM_COLORS.length]}`} />
                                    <span className="text-xs font-medium truncate max-w-[100px]">{emp.first_name} {emp.last_name?.charAt(0)}.</span>
                                  </div>
                                </td>
                                {wDays.map(day => {
                                  const dt = getTasksForEmpDay(emp.id, day);
                                  const isT = day === todayStrTeam;
                                  return (
                                    <td key={day} className={`border-b border-r px-1 py-1 align-top ${isT ? 'bg-blue-50/30' : ''}`}>
                                      <div className="space-y-0.5 max-h-24 overflow-y-auto">
                                        {dt.map(t => {
                                          const isDone = t.status === 'Terminé';
                                          const overdue = t.due_date && new Date(t.due_date) < new Date() && !isDone;
                                          const sStyle = getStatusStyle(t.status);
                                          const hasTime = t.scheduled_start_time && t.scheduled_end_time;
                                          return (
                                            <div key={t.id} className={`flex items-center gap-1 px-1.5 py-1 rounded ${isDone ? 'bg-green-50 dark:bg-green-950/40' : overdue ? 'bg-red-50 dark:bg-red-950/40' : 'bg-elevated-1'}`}>
                                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sStyle.dot}`} />
                                              <span className={`truncate font-medium ${isDone ? 'line-through text-text-muted' : overdue ? 'text-red-700 dark:text-red-300' : 'text-text-primary'}`}>{t.title}</span>
                                              {hasTime && <span className="text-[9px] text-text-muted flex-shrink-0 ml-auto">{t.scheduled_start_time}</span>}
                                              {t.vehicule_nom && <Truck className="w-2.5 h-2.5 text-orange-500 flex-shrink-0" />}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile : liste par jour */}
                    <div className="md:hidden divide-y">
                      {wDays.map(day => {
                        const d = new Date(day + 'T00:00:00');
                        const isT = day === todayStrTeam;
                        const dayAllTasks = activeEmps.flatMap(emp =>
                          getTasksForEmpDay(emp.id, day).map(t => ({ ...t, _emp: `${emp.first_name} ${emp.last_name?.charAt(0)}.` }))
                        );
                        if (dayAllTasks.length === 0) return null;
                        return (
                          <div key={day} className="px-3 py-2">
                            <p className={`text-xs font-bold mb-1 ${isT ? 'text-blue-700 dark:text-blue-300' : 'text-text-secondary'}`}>
                              {d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                              {isT && <span className="ml-1 text-blue-500 font-normal">(auj.)</span>}
                            </p>
                            <div className="space-y-1 pl-2 border-l-2 border-border-subtle">
                              {dayAllTasks.map(t => {
                                const sStyle = getStatusStyle(t.status);
                                const isDone = t.status === 'Terminé';
                                return (
                                  <div key={t.id} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${isDone ? 'bg-green-50 dark:bg-green-950/40' : 'bg-elevated-1'}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${sStyle.dot} flex-shrink-0`} />
                                    <span className={`font-medium truncate ${isDone ? 'line-through text-text-muted' : 'text-text-primary'}`}>{t.title}</span>
                                    <span className="text-[10px] text-text-muted flex-shrink-0">{t._emp}</span>
                                    <span className={`ml-auto px-1 py-0.5 rounded text-[10px] ${sStyle.bg} ${sStyle.text} flex-shrink-0`}>{t.status}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        };

        return <TeamWidget />;
      })()}

      {/* ═══ 3. EXTRAS DISPONIBLES — MINIMALISTE ═════════════════════════════ */}
      {(() => {
        const todayStr = new Date().toISOString().split('T')[0];
        const todaySchedules = presenceData.schedules.filter(s => s.date === todayStr);
        const allExtras = employees.filter(e => e.role_name === 'Extra');
        const extrasOccupesIds = new Set(
          todaySchedules
            .filter(s => ['Formation', 'Evenement', 'Évènement'].includes(s.entry_type))
            .map(s => s.employee_id)
        );
        const extrasDispo = allExtras.filter(e => !extrasOccupesIds.has(e.id));

        if (allExtras.length === 0) return null;

        return (
          <div className="bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 border-orange-300">
            <div className="px-3 sm:px-5 py-3 flex items-center justify-between bg-orange-50/60 border-b border-orange-100">
              <div className="flex items-center gap-2.5">
                <UserCheck className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                <span className="text-sm font-bold text-orange-800 dark:text-orange-200">Extras Disponibles</span>
                <span className="bg-orange-200/70 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">{extrasDispo.length}/{allExtras.length}</span>
              </div>
              <button onClick={() => onNavigate?.('pointeuse')}
                className="text-xs text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:text-orange-200 flex items-center gap-1">
                Pointeuse <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <div className="px-3 sm:px-5 py-3">
              {extrasDispo.length === 0 ? (
                <p className="text-xs text-text-muted text-center py-1">Aucun extra disponible aujourd'hui</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {extrasDispo.map(emp => (
                    <span key={emp.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 dark:bg-orange-950/40 border border-orange-200/60 rounded-full text-xs font-medium text-text-secondary hover:bg-orange-100 dark:bg-orange-900/40 transition">
                      <span className="w-5 h-5 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-[9px] shrink-0">
                        {(emp.first_name?.[0] || '')}{(emp.last_name?.[0] || '')}
                      </span>
                      {emp.first_name} {emp.last_name?.[0]}.
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ═══ 3. KPI ÉVÉNEMENTS ════════════════════════════════════════════════ */}
      {kpiEvents && (
        <div className="bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 border-orange-400">
          <SectionHeader
            icon={Calendar} title="Événements"
            count={(kpiEvents.nombre_evenements || 0) + (kpiEvents.upcoming?.length || 0)}
            color="bg-orange-50 dark:bg-orange-950/40 text-orange-800"
            action={
              <button onClick={() => onNavigate?.('evenements')}
                className="text-xs text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:text-orange-200 flex items-center gap-1">
                Voir tous <ArrowRight className="w-3 h-3" />
              </button>
            }
          />
          <div className="p-3 sm:p-4 space-y-3">
            {/* Stats du mois en cours */}
            {kpiEvents.nombre_evenements > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <StatCard label="Ce mois" value={kpiEvents.nombre_evenements}
                  sub="Événement(s)" icon={Calendar}
                  gradient="bg-gradient-to-br from-orange-500 to-amber-600" />
                <StatCard label="CA total" value={fmtEuro(kpiEvents.ca_total || 0)}
                  sub="Budget / CA réel" icon={Euro}
                  gradient="bg-gradient-to-br from-emerald-500 to-green-600" />
                {kpiEvents.top_extras?.length > 0 && (
                  <StatCard label="Top Extra" value={`${kpiEvents.top_extras[0].first_name} ${kpiEvents.top_extras[0].last_name?.charAt(0)}.`}
                    sub={`${kpiEvents.top_extras[0].nb_events} événement(s)`} icon={Award}
                    gradient="bg-gradient-to-br from-violet-500 to-purple-600" />
                )}
              </div>
            )}

            {/* Événements du mois */}
            {kpiEvents.evenements?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide">Ce mois</p>
                {kpiEvents.evenements.slice(0, 5).map(ev => (
                  <div key={ev.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-2 bg-elevated-1 rounded-lg text-sm gap-1 sm:gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${ev.statut === 'termine' ? 'bg-green-500' : ev.statut === 'en_cours' ? 'bg-blue-500' : 'bg-amber-400'}`} />
                      <span className="font-medium text-text-primary truncate">{ev.nom}</span>
                      <span className="text-xs text-text-muted shrink-0">{fmtDate(ev.date_debut)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-text-muted"><Users className="w-3 h-3 inline mr-0.5" />{ev.nb_participants}</span>
                      {ev.ca > 0 && <span className="font-semibold text-emerald-600 dark:text-emerald-400">{fmtEuro(ev.ca)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Événements à venir (prochains mois) */}
            {kpiEvents.upcoming?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide">À venir</p>
                {kpiEvents.upcoming.slice(0, 5).map(ev => (
                  <div key={ev.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-2 bg-orange-50/50 rounded-lg text-sm border border-orange-100 gap-1 sm:gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <span className="w-2 h-2 rounded-full bg-orange-400 shrink-0" />
                      <span className="font-medium text-text-primary truncate">{ev.nom}</span>
                      <span className="text-xs text-orange-600 dark:text-orange-400 font-medium shrink-0">{fmtDate(ev.date_debut)}</span>
                      {ev.lieu && <span className="text-xs text-text-muted truncate">{ev.lieu}</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-text-muted"><Users className="w-3 h-3 inline mr-0.5" />{ev.nb_participants}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Aucun événement */}
            {kpiEvents.nombre_evenements === 0 && (!kpiEvents.upcoming || kpiEvents.upcoming.length === 0) && (
              <p className="text-sm text-text-muted text-center py-4">Aucun événement planifié</p>
            )}
          </div>
        </div>
      )}

      {/* ═══ 4. TÂCHES EN RETARD + EN COURS — 2 COLONNES ════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Tâches en retard */}
        <div className="bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 border-red-400">
          <SectionHeader icon={AlertCircle} title="Tâches en retard" count={taches_en_retard.length}
            color="bg-red-50 dark:bg-red-950/40 text-red-800"
            action={
              <button onClick={() => onNavigate?.('taches')}
                className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:text-red-200 flex items-center gap-1">
                Voir toutes <ArrowRight className="w-3 h-3" />
              </button>
            }
          />
          {taches_en_retard.length === 0 ? (
            <div className="p-4 sm:p-6 text-center text-text-muted text-sm">Aucune tâche en retard</div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
              {taches_en_retard.slice(0, 8).map(task => {
                const dLate = Math.floor((new Date() - new Date(task.due_date)) / 86400000);
                return (
                  <div key={task.id} className="px-3 sm:px-4 py-2.5 hover:bg-red-50/40 transition">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-text-primary text-sm leading-tight flex-1 truncate">{task.title}</p>
                      <span className="text-red-600 dark:text-red-400 text-[11px] font-bold shrink-0">J+{dLate}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-text-muted">
                      <span>{task.assigned_to_name || task.service_name || '—'}</span>
                      <span className="text-red-500">{fmtDate(task.due_date)}</span>
                      {task.project_name && <span className="bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded-full">{task.project_name}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {taches_en_retard.length > 8 && (
            <div className="px-4 py-2 text-xs text-text-muted bg-elevated-1 text-center border-t">
              +{taches_en_retard.length - 8} autres
              <button onClick={() => onNavigate?.('taches')} className="ml-2 text-blue-600 dark:text-blue-400 underline">voir toutes</button>
            </div>
          )}
        </div>

        {/* Tâches en cours */}
        <div className="bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 border-blue-400">
          <SectionHeader icon={Activity} title="Tâches en cours" count={taches_en_cours.length}
            color="bg-blue-50 dark:bg-blue-950/40 text-blue-800"
            action={
              <button onClick={() => onNavigate?.('taches')}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:text-blue-200 flex items-center gap-1">
                Voir toutes <ArrowRight className="w-3 h-3" />
              </button>
            }
          />
          {taches_en_cours.length === 0 ? (
            <div className="p-4 sm:p-6 text-center text-text-muted text-sm">Aucune tâche en cours</div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
              {taches_en_cours.slice(0, 8).map(task => (
                <div key={task.id} className="px-3 sm:px-4 py-2.5 hover:bg-blue-50/40 transition">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-text-primary text-sm leading-tight flex-1 truncate">{task.title}</p>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                      task.priority === 'Critique' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' :
                      task.priority === 'Haute'    ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                      task.priority === 'Moyenne'  ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' :
                      'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'}`}>{task.priority || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-text-muted">
                    <span>{task.assigned_to_name || task.service_name || '—'}</span>
                    {task.due_date && <span>{fmtDate(task.due_date)}</span>}
                    {task.project_name && <span className="bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded-full">{task.project_name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {taches_en_cours.length > 8 && (
            <div className="px-4 py-2 text-xs text-text-muted bg-elevated-1 text-center border-t">
              +{taches_en_cours.length - 8} autres
            </div>
          )}
        </div>
      </div>

      {/* ═══ KPI & FINANCES — COMPACT ═══════════════════════════════════════ */}
      {data.kpi && (
        <div className="bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 border-emerald-400">
          <div className="px-3 sm:px-5 py-2.5 flex items-center justify-between bg-emerald-50 dark:bg-emerald-950/40 border-b">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-700" />
              <h3 className="text-sm font-bold text-emerald-800">KPI & Finances</h3>
            </div>
            <button onClick={() => onNavigate?.('kpi')}
              className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:text-emerald-200 flex items-center gap-1">
              Détails <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="px-3 sm:px-5 py-3 grid grid-cols-3 sm:grid-cols-6 gap-y-3 gap-x-2 sm:gap-x-0 sm:divide-x sm:divide-gray-200">
            {[
              { icon: Euro, label: 'CA mois', value: fmtEuro(data.kpi.montant_ht_mois || 0), color: 'text-emerald-600 dark:text-emerald-400' },
              { icon: CheckSquare, label: 'Signatures', value: data.kpi.signatures_mois || 0, color: 'text-cyan-600 dark:text-cyan-400' },
              { icon: Briefcase, label: 'Budget actif', value: fmtEuro(data.kpi.budget_projets_actifs || 0), color: 'text-violet-600 dark:text-violet-400' },
              { icon: AlertTriangle, label: 'En alerte', value: data.kpi.objectifs_en_alerte || 0, color: (data.kpi.objectifs_en_alerte || 0) > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-text-muted' },
              { icon: Clock, label: 'En retard', value: data.kpi.objectifs_en_retard || 0, color: (data.kpi.objectifs_en_retard || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-text-muted' },
              { icon: TrendingUp, label: 'Atteinte', value: `${data.kpi.taux_atteinte_global || 0}%`, color: (data.kpi.taux_atteinte_global || 0) >= 80 ? 'text-emerald-600 dark:text-emerald-400' : (data.kpi.taux_atteinte_global || 0) >= 50 ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400' },
            ].map((kpi, i) => (
              <div key={i} className="flex flex-col items-center sm:px-3 text-center">
                <kpi.icon className={`w-4 h-4 ${kpi.color} mb-0.5`} />
                <p className={`text-lg sm:text-xl font-bold ${kpi.color} leading-tight`}>{kpi.value}</p>
                <p className="text-[10px] text-text-muted font-medium uppercase tracking-wide">{kpi.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ 5. PROJETS + OBJECTIFS — 2 COLONNES ════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

      {/* Projets */}
      <div className="bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 border-violet-400">
        <SectionHeader icon={Folder} title="Projets" count={projets.length}
          color="bg-violet-50 dark:bg-violet-950/40 text-violet-800"
          action={
            <button onClick={() => onNavigate?.('projets')}
              className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-1 bg-violet-100 dark:bg-violet-900/40 px-3 py-1 rounded-lg">
              <Plus className="w-3 h-3" /> Nouveau projet
            </button>
          }
        />
        <div className="p-3 sm:p-4 space-y-3">
          {projets.length === 0 ? (
            <div className="text-center py-10 text-text-muted">
              <Folder className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucun projet —{' '}
                <button onClick={() => onNavigate?.('projets')} className="text-violet-600 dark:text-violet-400 underline">créer un projet</button>
              </p>
            </div>
          ) : projets.map(projet => {
            const isExpanded = expandedProj.includes(projet.id);
            const dl = daysLeft(projet.end_date);
            const isLate = dl !== null && dl < 0 && projet.status === 'En cours';
            const isSoon = dl !== null && dl >= 0 && dl <= 7 && projet.status === 'En cours';
            const pct = projet.progression || 0;

            return (
              <div key={projet.id} className={`border rounded-xl overflow-hidden transition ${isLate ? 'border-red-200 dark:border-red-800 bg-red-50/30' : isSoon ? 'border-amber-200 dark:border-amber-800 bg-amber-50/30' : 'border-border-subtle'}`}>
                {/* Header projet */}
                <div className="p-3 sm:p-4 flex items-start gap-2 sm:gap-3">
                  <button onClick={() => toggleProj(projet.id)} className="mt-1 shrink-0 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center">
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    {/* Ligne titre + statut */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-text-primary text-sm">{projet.name}</h3>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                        projet.status === 'En cours'   ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' :
                        projet.status === 'Terminé'    ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                        projet.status === 'En attente' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' :
                        'bg-elevated-1 text-text-secondary'}`}>{projet.status}</span>
                      {isLate  && <span className="text-[11px] bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full font-medium flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" />Retard J{dl}</span>}
                      {isSoon  && <span className="text-[11px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full font-medium flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{dl}j restants</span>}
                      {projet.alerte_somme && <span className="text-[11px] bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full font-medium flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" />Σ ≠ cible</span>}
                    </div>

                    {/* Chef de projet + client + budget + dates */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-text-muted mb-2">
                      {(projet.chef_de_projet_name || projet.created_by_name) && (
                        <span className="flex items-center gap-1 text-violet-700 dark:text-violet-300 font-medium">
                          <User className="w-3 h-3" />
                          {projet.chef_de_projet_name || projet.created_by_name}
                        </span>
                      )}
                      {projet.client && (
                        <span className="flex items-center gap-1">🏢 {projet.client}</span>
                      )}
                      {projet.chiffre && (
                        <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400 font-medium">
                          <Hash className="w-3 h-3" />{projet.chiffre} {projet.unite || ''}
                        </span>
                      )}
                      {projet.budget > 0 && (
                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <DollarSign className="w-3 h-3" />{fmt(projet.budget)} €
                        </span>
                      )}
                      {projet.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {fmtDate(projet.start_date)} → {fmtDate(projet.end_date)}
                        </span>
                      )}
                    </div>

                    {/* Barre progression tâches */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1">
                        <ProgressBar pct={pct} color={pctColor(pct)} height="h-2" />
                      </div>
                      <span className="text-xs font-bold text-text-secondary w-10 text-right">{pct}%</span>
                    </div>

                    {/* Mini stats */}
                    <div className="flex flex-wrap gap-2 text-[11px]">
                      <span className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <CheckSquare className="w-3 h-3" />{projet.done_tasks || 0} terminées
                      </span>
                      <span className="bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Clock className="w-3 h-3" />{projet.in_progress_tasks || 0} en cours
                      </span>
                      <span className="bg-elevated-1 text-text-secondary px-2 py-0.5 rounded-full">{projet.todo_tasks || 0} à faire</span>
                      {projet.objectif_count > 0 && (
                        <span className="bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Target className="w-3 h-3" />{projet.objectif_count} objectif{projet.objectif_count > 1 ? 's' : ''}
                        </span>
                      )}
                      {projet.late_tasks > 0 && (
                        <span className="bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />{projet.late_tasks} en retard
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bouton accès workspace */}
                  <button
                    onClick={() => onNavigate?.('projets')}
                    className="shrink-0 bg-violet-100 dark:bg-violet-900/40 hover:bg-violet-200 dark:bg-violet-800/40 text-violet-700 dark:text-violet-300 px-2 py-1.5 rounded-lg text-[11px] font-medium flex items-center gap-1 min-h-[44px] sm:min-h-0"
                    title="Ouvrir dans Workspace"
                  >
                    <Eye className="w-3 h-3" /> Ouvrir
                  </button>
                </div>

                {/* Objectifs du projet (dépliable) */}
                {isExpanded && (
                  <div className="border-t border-border-subtle px-3 sm:px-4 pb-3 sm:pb-4 bg-elevated-1/50">
                    <ProjetObjectifsMini
                      projectId={projet.id}
                      authToken={authToken}
                      onNavigate={onNavigate}
                      onAddAction={(obj) => {
                        setTargetObjectif(obj);
                        setActionForm({ title: '', assigned_to: authUser?.employee_id || '', due_date: '', priority: 'Moyenne' });
                        setShowAddAction(true);
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══ OBJECTIFS ═════════════════════════════════════════════════════════ */}
      <div className="bg-surface rounded-2xl shadow-md overflow-hidden border-l-4 border-amber-400">
        <SectionHeader icon={Target} title="Objectifs & Prévisions" count={objectifs.length}
          color="bg-amber-50 dark:bg-amber-950/40 text-amber-800"
          action={
            <button onClick={() => onNavigate?.('objectifs')}
              className="text-xs text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:text-amber-200 flex items-center gap-1">
              Voir tout <ArrowRight className="w-3 h-3" />
            </button>
          }
        />
        <div className="p-3 sm:p-4 space-y-3">
          {objectifs.length === 0 ? (
            <div className="text-center py-10 text-text-muted">
              <Target className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucun objectif actif</p>
            </div>
          ) : objectifs.filter(o => !o.parent_objectif_id).map(obj => {
            const isExpanded = expandedObj.includes(obj.id);
            const valPct = obj.chiffrage > 0
              ? Math.min(100, Math.round((obj.valeur_actuelle / obj.chiffrage) * 100))
              : null;
            const actPct = obj.total_actions > 0
              ? Math.round((obj.actions_terminees / obj.total_actions) * 100)
              : 0;
            const dl = daysLeft(obj.date_fin);
            const isAtRisk    = valPct !== null && valPct < 30 && dl !== null && dl < 14;
            const isAlmostDone = valPct !== null && valPct >= 80;

            return (
              <div key={obj.id} className={`border rounded-xl p-3 sm:p-4 transition ${isAtRisk ? 'border-red-200 dark:border-red-800 bg-red-50/20' : isAlmostDone ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/20' : 'border-border-subtle'}`}>
                <div className="flex items-start gap-2 sm:gap-3">
                  <button onClick={() => toggleObj(obj.id)} className="mt-0.5 shrink-0 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center">
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-text-primary text-sm">{obj.titre}</h3>
                      {obj.project_name && (
                        <span className="text-[11px] bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 px-2 py-0.5 rounded-full">{obj.project_name}</span>
                      )}
                      {isAtRisk && (
                        <span className="text-[11px] bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full flex items-center gap-0.5">
                          <AlertTriangle className="w-2.5 h-2.5" /> Risque
                        </span>
                      )}
                      {isAlmostDone && (
                        <span className="text-[11px] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-0.5">
                          <Award className="w-2.5 h-2.5" /> Quasi atteint
                        </span>
                      )}
                      {obj.alerte_somme && (
                        <span className="text-[11px] bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full flex items-center gap-0.5">
                          <AlertTriangle className="w-2.5 h-2.5" /> Σ ≠ cible
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-3 text-xs text-text-muted mb-2">
                      {obj.proprietaire_nom && (
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{obj.proprietaire_nom}</span>
                      )}
                      {obj.date_fin && (
                        <span className={`flex items-center gap-1 ${dl !== null && dl < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : dl !== null && dl <= 7 ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}`}>
                          <Calendar className="w-3 h-3" />
                          {fmtDate(obj.date_fin)}
                          {dl !== null && ` (${dl < 0 ? `J${dl}` : dl === 0 ? "Aujourd'hui" : `${dl}j`})`}
                        </span>
                      )}
                    </div>

                    {/* Progression valeur réelle */}
                    {obj.chiffrage > 0 && (
                      <div className="mb-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-text-secondary">
                            Réel : <strong>{fmt(obj.valeur_actuelle)}</strong> / {fmt(obj.chiffrage)} {obj.unite || 'unité'}
                          </span>
                          <span className={`font-bold ${valPct >= 100 ? 'text-emerald-600 dark:text-emerald-400' : valPct >= 50 ? 'text-blue-600 dark:text-blue-400' : 'text-red-500'}`}>
                            {valPct}%
                          </span>
                        </div>
                        <ProgressBar pct={valPct} color={pctColor(valPct)} height="h-2.5" />
                      </div>
                    )}

                    {/* Progression actions + compteurs */}
                    <div className="flex flex-wrap gap-2 text-[11px] mb-2">
                      {obj.sous_objectifs_count > 0 && (
                        <span className="bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Layers className="w-3 h-3" />{obj.sous_objectifs_count} sous-obj.
                        </span>
                      )}
                      {obj.total_actions > 0 && (
                        <span className="bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">
                          {obj.actions_terminees}/{obj.total_actions} actions ({actPct}%)
                        </span>
                      )}
                    </div>
                    {obj.total_actions > 0 && (
                      <div className="mb-2">
                        <ProgressBar pct={actPct} color="bg-purple-400" height="h-1.5" />
                      </div>
                    )}

                    {/* Prévisions */}
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mt-2">
                      {[
                        { label: '24h',      val: obj.actions_prevues_1j, bg: 'bg-blue-50 dark:bg-blue-950/40',    text: 'text-blue-700 dark:text-blue-300' },
                        { label: '7 jours',  val: obj.actions_prevues_1s, bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300' },
                        { label: '30 jours', val: obj.actions_prevues_1m, bg: 'bg-violet-50 dark:bg-violet-950/40',  text: 'text-violet-700 dark:text-violet-300' },
                      ].map(({ label, val, bg, text }) => (
                        <div key={label} className={`${bg} rounded-lg p-2 text-center`}>
                          <p className={`text-[10px] font-medium ${text}`}>{label}</p>
                          <p className={`text-lg font-bold ${text}`}>{val || 0}</p>
                          <p className={`text-[10px] ${text} opacity-70`}>action{(val || 0) > 1 ? 's' : ''}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bouton action rapide */}
                  <button
                    onClick={() => {
                      setTargetObjectif(obj);
                      setActionForm({ title: '', assigned_to: authUser?.employee_id || '', due_date: '', priority: 'Moyenne' });
                      setShowAddAction(true);
                    }}
                    className="shrink-0 bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:bg-amber-800/40 text-amber-700 dark:text-amber-300 p-2 rounded-lg min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                    title="Ajouter une action"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* Décomposition mensuelle */}
                {isExpanded && (
                  <ObjectifDecompositionMini objectifId={obj.id} authToken={authToken} unite={obj.unite} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      </div>{/* fin grid Projets + Objectifs */}

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sous-composant : Objectifs d'un projet (chargement lazy)
// ─────────────────────────────────────────────────────────────────────────────
const ProjetObjectifsMini = ({ projectId, authToken, onNavigate, onAddAction }) => {
  const [objectifs, setObjectifs] = useState([]);
  const [loaded, setLoaded]       = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/objectifs?project_id=${projectId}`, {
      headers: { 'Authorization': `Bearer ${authToken}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(d => { setObjectifs(Array.isArray(d) ? d : []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [projectId, authToken]);

  if (!loaded) return (
    <p className="text-xs text-text-muted mt-3 flex items-center gap-1">
      <RefreshCw className="w-3 h-3 animate-spin" />Chargement…
    </p>
  );
  if (objectifs.length === 0) return (
    <div className="mt-3 text-xs text-text-muted flex items-center justify-between">
      <span>Aucun objectif lié à ce projet</span>
      <button onClick={() => onNavigate?.('projets')} className="text-violet-600 dark:text-violet-400 underline">Ajouter</button>
    </div>
  );

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-semibold text-text-muted uppercase tracking-wide">Objectifs ({objectifs.length})</p>
      {objectifs.map(obj => {
        const pct = obj.chiffrage > 0 ? Math.min(100, Math.round(((obj.valeur_actuelle || 0) / obj.chiffrage) * 100)) : null;
        return (
          <div key={obj.id} className="bg-surface border border-amber-100 rounded-lg p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-text-secondary truncate">{obj.titre}</span>
                {pct !== null && (
                  <span className={`text-[11px] font-bold ${pct >= 100 ? 'text-emerald-600 dark:text-emerald-400' : pct >= 50 ? 'text-blue-600 dark:text-blue-400' : 'text-red-500'}`}>
                    {pct}%
                  </span>
                )}
              </div>
              {obj.chiffrage > 0 && (
                <div className="mt-1">
                  <ProgressBar pct={pct} color={pctColor(pct)} height="h-1.5" />
                  <p className="text-[10px] text-text-muted mt-0.5">
                    {fmt(obj.valeur_actuelle || 0)} / {fmt(obj.chiffrage)} {obj.unite}
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={() => onAddAction(obj)}
              className="shrink-0 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 p-1.5 rounded-lg"
              title="Action rapide"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sous-composant : Décomposition mensuelle d'un objectif (lazy)
// ─────────────────────────────────────────────────────────────────────────────
const MONTHS_SHORT = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

const ObjectifDecompositionMini = ({ objectifId, authToken, unite }) => {
  const [decomp, setDecomp] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/objectifs/${objectifId}/decomposition`, {
      headers: { 'Authorization': `Bearer ${authToken}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(setDecomp)
      .catch(() => {});
  }, [objectifId, authToken]);

  if (!decomp || !decomp.months || decomp.months.length === 0) return null;

  return (
    <div className="mt-3 border-t border-border-subtle pt-3">
      <div className="flex items-center gap-2 mb-2">
        <Layers className="w-3.5 h-3.5 text-amber-500" />
        <span className="text-[11px] font-semibold text-text-muted uppercase tracking-wide">Décomposition mensuelle</span>
        {decomp.alert_sum && (
          <span className="text-[11px] bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full flex items-center gap-0.5">
            <AlertTriangle className="w-2.5 h-2.5" /> Σ ≠ cible
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
        {decomp.months.map(m => {
          const pct = m.valeur_cible > 0 ? Math.min(100, Math.round(((m.valeur_actuelle || 0) / m.valeur_cible) * 100)) : 0;
          const warnWeeks = m.weeks && m.weeks.length > 0 &&
            Math.abs(m.weeks.reduce((s, w) => s + (w.valeur_cible || 0), 0) - m.valeur_cible) > 0.01;
          return (
            <div key={m.id} className="bg-amber-50 dark:bg-amber-950/40 border border-amber-100 rounded-lg p-2 text-center">
              <p className="text-[10px] font-bold text-amber-700 dark:text-amber-300">{MONTHS_SHORT[m.month - 1]} {m.year}</p>
              <p className="text-xs font-bold text-text-secondary">{fmt(m.valeur_cible)}</p>
              <div className="mt-1">
                <ProgressBar pct={pct} color={pctColor(pct)} height="h-1" />
              </div>
              <p className="text-[10px] text-text-muted mt-0.5">{fmt(m.valeur_actuelle || 0)} réel</p>
              {warnWeeks && (
                <AlertTriangle className="w-2.5 h-2.5 text-orange-400 mx-auto mt-0.5" title="Σ semaines ≠ mensuel" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardPatron;
